import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/lib/utils'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        success:
          'border-transparent bg-emerald-500 text-white [a&]:hover:bg-emerald-600 dark:bg-emerald-600 dark:text-white dark:hover:bg-emerald-700 focus-visible:ring-emerald-500/20 dark:focus-visible:ring-emerald-600/40',
        info:
          'border-transparent bg-sky-500 text-white [a&]:hover:bg-sky-600 dark:bg-sky-600 dark:text-white dark:hover:bg-sky-700 focus-visible:ring-sky-500/20 dark:focus-visible:ring-sky-600/40',
        warning:
          'border-transparent bg-amber-500 text-white [a&]:hover:bg-amber-600 dark:bg-amber-600 dark:text-white dark:hover:bg-amber-700 focus-visible:ring-amber-500/20 dark:focus-visible:ring-amber-600/40',
        'light-gray':
          'border-transparent bg-gray-400/10 text-gray-400 inset-ring inset-ring-gray-400/20',
        'light-red':
          'border-transparent bg-red-400/10 text-red-400 inset-ring inset-ring-red-400/20',
        'light-yellow':
          'border-transparent bg-yellow-400/10 text-yellow-500 inset-ring inset-ring-yellow-400/20',
        'light-green':
          'border-transparent bg-green-400/10 text-green-400 inset-ring inset-ring-green-500/20',
        'light-blue':
          'border-transparent bg-blue-400/10 text-blue-400 inset-ring inset-ring-blue-400/30',
        'light-indigo':
          'border-transparent bg-indigo-400/10 text-indigo-400 inset-ring inset-ring-indigo-400/30',
        'light-purple':
          'border-transparent bg-purple-400/10 text-purple-400 inset-ring inset-ring-purple-400/30',
        'light-pink':
          'border-transparent bg-pink-400/10 text-pink-400 inset-ring inset-ring-pink-400/20',
        'light-success':
          'border-transparent bg-green-400/10 text-green-400 inset-ring inset-ring-green-500/20'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
